import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { NAVIGATION } from '../../containers/App/constants';
import { useSearch } from '../../contexts/Search';
import { DEFAULT_SEARCH_PARAMETERS } from '../../contexts/Search/constants';
import { getPriceCalendarByRange } from '../../services/priceCalendar';
import { PriceCalendar } from '../../services/priceCalendar/types';
import {
  PriceCalendarBodyProps,
  PriceCalendarDayProps,
  PriceCalendarSetCheapestDate,
} from './types';

type UsePriceCalendarBodyPayload = {
  start: Dayjs;
  end: Dayjs;
  setCheapestDate: PriceCalendarSetCheapestDate;
} & Pick<PriceCalendarBodyProps, 'origin' | 'destination'>;

export const usePriceCalendarBody = (payload: UsePriceCalendarBodyPayload) => {
  const { destination, end, origin, setCheapestDate, start } = payload;
  const searchContext = useSearch();

  const [priceCalendar, setPriceCalendar] = useState<PriceCalendar | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const dateWithPrice =
    priceCalendar?.filter((date) => date.trip !== null) || [];
  const cheapestDate = dateWithPrice.reduce((cheapest, current) => {
    return current.trip.priceCents < (cheapest?.trip.priceCents || Infinity)
      ? current
      : cheapest;
  }, null);

  const originId = origin.id.toString();
  const destinationId = destination.id.toString();

  useEffect(() => {
    if (!start || !end || !originId || !destinationId) {
      return;
    }

    setIsLoading(true);

    getPriceCalendarByRange({
      destinationId,
      originId,
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    })
      .then((calendar) => setPriceCalendar(calendar))
      .finally(() => setIsLoading(false));
  }, [destinationId, end, originId, start]);

  useEffect(() => {
    setCheapestDate?.(cheapestDate || null);
  }, [cheapestDate, setCheapestDate]);

  const getDayProps = ({
    day,
    dayIndex,
    weekIndex,
  }: {
    day: Dayjs;
    weekIndex: number;
    dayIndex: number;
  }): PriceCalendarDayProps => {
    const priceCalendarIndex = weekIndex * 7 + dayIndex;
    const trip = priceCalendar ? priceCalendar[priceCalendarIndex].trip : null;
    const isCheapest = trip
      ? trip.priceCents === cheapestDate.trip.priceCents
      : false;
    const today = dayjs();
    const isInPast = day.isBefore(today, 'date');
    const isToday = day.isSame(today, 'date');

    const searchParameters = searchContext.createSearchUrl({
      ...DEFAULT_SEARCH_PARAMETERS,
      originId: origin.id,
      origin: origin.name,
      destinationId: destination.id,
      destination: destination.name,
      outboundDate: day.format('YYYY-MM-DD'),
    });

    const searchLink = isInPast
      ? undefined
      : NAVIGATION.RESULTS + searchParameters;

    return {
      trip,
      isCheapest,
      searchLink,
      isLoading,
      isInPast,
      isToday,
      day: day.format('D'),
    };
  };

  return {
    getDayProps,
  };
};
